import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Spinner, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const COLOR = {
  primary:     '#0d9488', // Standard vibrant teal
  primaryDk:   '#0f766e', // Medium dark teal
  primaryLt:   '#ccfbf1',
  primaryMd:   '#5eead4',
  topHeaderBg: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
  headerBg:    '#f8fafc',
  headerBgSub: '#f1f5f9',
  headerText:  '#1e293b',
  headerBorder:'#cbd5e1',
  danger:      '#dc2626',
  dangerDk:    '#b91c1c',
  dangerLt:    '#fee2e2',
  warn:        '#d97706',
  warnLt:      '#fef3c7',
  info:        '#0284c7',
  infoLt:      '#e0f2fe',
  slate50:     '#f8fafc',
  slate100:    '#f1f5f9',
  slate200:    '#e2e8f0',
  slate300:    '#cbd5e1',
  slate400:    '#94a3b8',
  slate600:    '#475569',
  slate700:    '#334155',
  slate800:    '#1e293b',
  slate900:    '#0f172a',
  white:       '#ffffff',
};

const S = {
  input: {
    border: `1px solid ${COLOR.slate200}`, borderRadius: '6px',
    fontSize: '0.76rem', padding: '3px 8px', outline: 'none',
    background: COLOR.white, color: COLOR.slate800, height: '28px',
    transition: 'border-color .15s, box-shadow .15s', width: '100%',
  },
  label: {
    fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.4px', color: COLOR.slate600, marginBottom: '2px', display: 'block',
  },
  btnPrimary: {
    background: `linear-gradient(135deg, ${COLOR.primary} 0%, ${COLOR.primaryDk} 100%)`,
    border: 'none', borderRadius: '6px', color: COLOR.white, fontWeight: 700,
    fontSize: '0.75rem', padding: '4px 12px', cursor: 'pointer', height: '28px',
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    transition: 'opacity .15s, transform .1s', letterSpacing: '0.2px',
  },
  btnDanger: {
    background: `linear-gradient(135deg, ${COLOR.danger} 0%, ${COLOR.dangerDk} 100%)`,
    border: 'none', borderRadius: '6px', color: COLOR.white, fontWeight: 700,
    fontSize: '0.75rem', padding: '4px 14px', cursor: 'pointer', height: '28px',
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },
  btnGhost: {
    background: 'transparent', border: `1px solid ${COLOR.slate200}`, borderRadius: '6px',
    color: COLOR.slate600, fontWeight: 600, fontSize: '0.74rem', padding: '3px 10px', height: '28px',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px',
    transition: 'background .15s',
  },
  card: {
    background: COLOR.white, borderRadius: '8px',
    border: `1px solid ${COLOR.slate200}`, boxShadow: '0 1px 3px rgba(0,0,0,.04)',
  },
};

const PercentBadge = ({ value }) => {
  const v = parseFloat(value) || 0;
  if (v === 0) return <span style={{ color: COLOR.slate300, fontSize: '0.74rem', fontWeight: 600 }}>0%</span>;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: '44px', padding: '1px 6px',
      background: v >= 50 ? '#dcfce7' : v >= 30 ? '#fef9c3' : '#fee2e2',
      color:      v >= 50 ? '#15803d' : v >= 30 ? '#92400e' : '#b91c1c',
      border:     `1px solid ${v >= 50 ? '#bbf7d0' : v >= 30 ? '#fde68a' : '#fca5a5'}`,
      borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700,
    }}>
      {v}%
    </span>
  );
};

const JenisBadge = ({ jenis }) => {
  const isPen = jenis && jenis.toLowerCase().includes('pendamping') && !jenis.toLowerCase().includes('non');
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '2px 8px', borderRadius: '12px', fontSize: '0.68rem', fontWeight: 700,
      background: isPen ? '#f0f9ff' : COLOR.slate100,
      color:      isPen ? '#0284c7' : COLOR.slate600,
      border: `1px solid ${isPen ? '#bae6fd' : COLOR.slate200}`,
    }}>
      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: isPen ? '#0284c7' : COLOR.slate400, display: 'inline-block' }} />
      {isPen ? 'Pendamping' : 'Non Pendam'}
    </span>
  );
};

const NumInput = ({ value, onChange, min = 0, max = 100, style = {} }) => (
  <input
    type="number" min={min} max={max} step="0.1" value={value}
    onChange={e => onChange(parseFloat(e.target.value) || 0)}
    style={{ ...S.input, textAlign: 'center', fontWeight: 700, ...style }}
    onFocus={e => { e.target.style.borderColor = COLOR.primary; e.target.style.boxShadow = `0 0 0 2px ${COLOR.primaryLt}`; }}
    onBlur={e => { e.target.style.borderColor = COLOR.slate200; e.target.style.boxShadow = 'none'; }}
  />
);

const SettingCard = ({ label, value, onChange, accent, light }) => (
  <div style={{
    background: light, border: `1px solid ${accent}30`, borderRadius: '6px',
    padding: '4px 8px', display: 'flex', alignItems: 'center', gap: '8px',
  }}>
    <div style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.3px', color: accent, textTransform: 'uppercase' }}>
      {label}:
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
      <input
        type="number" min={0} max={100} step={1} value={value}
        onChange={e => onChange(parseInt(e.target.value) || 0)}
        style={{
          width: '45px', textAlign: 'center', fontWeight: 800, fontSize: '0.85rem',
          border: `1.5px solid ${accent}50`, borderRadius: '4px', padding: '1px 3px', height: '24px',
          background: COLOR.white, color: accent, outline: 'none',
        }}
        onFocus={e => { e.target.style.borderColor = accent; e.target.style.boxShadow = `0 0 0 2px ${accent}20`; }}
        onBlur={e => { e.target.style.borderColor = `${accent}50`; e.target.style.boxShadow = 'none'; }}
      />
      <span style={{ fontWeight: 800, fontSize: '0.78rem', color: accent }}>%</span>
    </div>
  </div>
);

const AturanJasaMedisBPJS = () => {
  const navigate   = useNavigate();
  const getApiUrl  = () => localStorage.getItem('api_url') || 'http://localhost:8000';
  const getToken   = () => localStorage.getItem('token');

  // Active Tab: 'tindakan' | 'pendamping' | 'aturan'
  const [activeTab, setActiveTab] = useState('tindakan');

  // Tab 1: Data Aturan & Setting
  const [jasaList, setJasaList] = useState([]);
  const [isLoadingAturan, setIsLoadingAturan] = useState(true);

  // Tab 2: Master Tindakan (/show_tindakan)
  const [tindakanList, setTindakanList] = useState([]);
  const [isLoadingTindakan, setIsLoadingTindakan] = useState(false);
  const [searchTindakan, setSearchTindakan] = useState('');
  const [pageTindakan, setPageTindakan] = useState(1);

  // Tab 3: Master Pendamping (/show_pendamping)
  const [pendampingList, setPendampingList] = useState([]);
  const [isLoadingPendamping, setIsLoadingPendamping] = useState(false);
  const [searchPendamping, setSearchPendamping] = useState('');
  const [pagePendamping, setPagePendamping] = useState(1);

  // Modal Lookup
  const [modalLookupList, setModalLookupList] = useState([]);
  const [isSearchingLookup, setIsSearchingLookup] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [lookupQ, setLookupQ] = useState('');

  // Global Settings
  const [settingMedis, setSettingMedis] = useState({ operator: 20, operator1: 60, operator2: 40, naikbpjs: 75, operatorbersama: 11, cob: 13.0, cobbersama: 7.15 });
  const [isSavingSetting, setIsSavingSetting] = useState(false);
  const [clock, setClock] = useState({ date: '', time: '' });

  // Filters for Tab Aturan
  const [searchQuery, setSearchQuery] = useState('');
  const [filterJenis, setFilterJenis] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS = 10;

  // Form State
  const defForm = { jenis: 'Non Pendam', kd_tindakan: '', nm_tindakan: '', tarifnontindakan: 0, tariftindakan: 0, tarifbiasa: 0, cekvisit: 0 };
  const [formData, setFormData] = useState(defForm);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [toast, setToast] = useState({ show: false, type: 'success', msg: '' });
  const formRef = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ show: true, type, msg });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 3500);
  };

  // 1. Fetch Aturan & Setting
  const fetchAturanData = async () => {
    setIsLoadingAturan(true);
    try {
      const res = await fetch(`${getApiUrl()}/show_aturanjasamedisbpjs`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (Array.isArray(data.list_jasa)) setJasaList(data.list_jasa);
      if (data.setting) setSettingMedis(prev => ({ ...prev, ...data.setting }));
    } catch (e) { showToast('Gagal memuat aturan: ' + e.message, 'danger'); }
    finally { setIsLoadingAturan(false); }
  };

  // 2. Fetch Master Tindakan (/show_tindakan)
  const fetchTindakanData = async () => {
    setIsLoadingTindakan(true);
    try {
      const res = await fetch(`${getApiUrl()}/show_tindakan`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (Array.isArray(data.list_tindakan)) setTindakanList(data.list_tindakan);
    } catch (e) {
      console.warn('Gagal fetch /show_tindakan:', e);
    } finally {
      setIsLoadingTindakan(false);
    }
  };

  // 3. Fetch Master Pendamping (/show_pendamping)
  const fetchPendampingData = async () => {
    setIsLoadingPendamping(true);
    try {
      const res = await fetch(`${getApiUrl()}/show_pendamping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ search: '' })
      });
      const data = await res.json();
      if (Array.isArray(data.list_pendamping)) setPendampingList(data.list_pendamping);
    } catch (e) {
      console.warn('Gagal fetch /show_pendamping:', e);
    } finally {
      setIsLoadingPendamping(false);
    }
  };

  // Modal Lookup Data
  const fetchModalLookup = async (jenis = formData.jenis, searchParam = '') => {
    setIsSearchingLookup(true);
    const isPendamping = (jenis || '').toLowerCase().includes('pendamping') && !(jenis || '').toLowerCase().includes('non');
    const endpoint = isPendamping ? '/show_pendamping' : '/show_mastertindakan';

    try {
      const res = await fetch(`${getApiUrl()}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ search: searchParam })
      });
      const data = await res.json();
      if (isPendamping && Array.isArray(data.list_pendamping)) {
        setModalLookupList(data.list_pendamping);
      } else if (!isPendamping && Array.isArray(data.list_tindakan)) {
        setModalLookupList(data.list_tindakan);
      }
    } catch (err) {
      console.error(`Error fetch ${endpoint}:`, err);
    } finally {
      setIsSearchingLookup(false);
    }
  };

  useEffect(() => {
    fetchAturanData();
    fetchTindakanData();
    fetchPendampingData();

    const tick = () => {
      const now = new Date();
      setClock({
        date: now.toLocaleDateString('id-ID', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }),
        time: now.toLocaleTimeString('id-ID'),
      });
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async e => {
    if (e) e.preventDefault();
    if (!formData.kd_tindakan.trim()) { showToast('Kode tindakan / posisi wajib diisi!', 'warning'); return; }
    setIsSaving(true);
    try {
      const r = await (await fetch(`${getApiUrl()}/simpan_aturanjasamedisbpjs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(formData),
      })).json();
      if (r.status === 1) { showToast(r.msg || 'Data berhasil disimpan!'); fetchAturanData(); resetForm(); }
      else showToast(r.msg || 'Gagal menyimpan!', 'danger');
    } catch (e) { showToast('Error: ' + e.message, 'danger'); }
    finally { setIsSaving(false); }
  };

  const handleDelete = async item => {
    if (!window.confirm(`Hapus aturan untuk "${item.nm_tindakan}"?`)) return;
    try {
      const r = await (await fetch(`${getApiUrl()}/hapus_aturanjasamedisbpjs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ kd_tindakan: item.kd_tindakan }),
      })).json();
      if (r.status === 1) { showToast(r.msg || 'Aturan dihapus!'); fetchAturanData(); if (formData.kd_tindakan === item.kd_tindakan) resetForm(); }
      else showToast(r.msg || 'Gagal menghapus!', 'danger');
    } catch (e) { showToast('Error: ' + e.message, 'danger'); }
  };

  const handleSaveSetting = async () => {
    setIsSavingSetting(true);
    try {
      const r = await (await fetch(`${getApiUrl()}/simpan_settingmedisbpjs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(settingMedis),
      })).json();
      if (r.status === 1) showToast(r.msg || 'Setting berhasil disimpan!');
      else showToast(r.msg || 'Gagal menyimpan setting!', 'danger');
    } catch (e) { showToast('Error: ' + e.message, 'danger'); }
    finally { setIsSavingSetting(false); }
  };

  const editRow  = item => { setFormData({ ...defForm, ...item }); setIsEditing(true); formRef.current?.scrollIntoView({ behavior: 'smooth' }); };
  const resetForm = () => { setFormData(defForm); setIsEditing(false); };

  const handleOpenLookup = () => {
    setLookupQ('');
    fetchModalLookup(formData.jenis, '');
    setShowModal(true);
  };

  const handleJenisChange = (newJenis) => {
    setFormData(prev => ({
      ...prev,
      jenis: newJenis,
      kd_tindakan: isEditing ? prev.kd_tindakan : '',
      nm_tindakan: isEditing ? prev.nm_tindakan : ''
    }));
    fetchModalLookup(newJenis, '');
  };

  const handleSelectFromTindakanTab = (item) => {
    setFormData({
      ...defForm,
      jenis: 'Non Pendam',
      kd_tindakan: item.kd_tindakan || '',
      nm_tindakan: item.nm_tindakan || '',
    });
    setIsEditing(false);
    setActiveTab('aturan');
    showToast(`Tindakan "${item.nm_tindakan}" dimuat ke formulir aturan.`);
  };

  const handleSelectFromPendampingTab = (item) => {
    setFormData({
      ...defForm,
      jenis: 'Pendamping',
      kd_tindakan: item.kd_posisi || item.kd_tindakan || '',
      nm_tindakan: item.posisi || item.nm_tindakan || '',
    });
    setIsEditing(false);
    setActiveTab('aturan');
    showToast(`Pendamping "${item.posisi || item.nm_tindakan}" dimuat ke formulir aturan.`);
  };

  const isCurrentPendamping = (formData.jenis || '').toLowerCase().includes('pendamping') && !(formData.jenis || '').toLowerCase().includes('non');

  const lookupFiltered = useMemo(() => {
    if (!lookupQ) return modalLookupList.slice(0, 100);
    const q = lookupQ.toLowerCase();
    return modalLookupList.filter(t => {
      const kode = (t.kd_tindakan || t.kd_posisi || '').toLowerCase();
      const nama = (t.nm_tindakan || t.posisi || '').toLowerCase();
      return kode.includes(q) || nama.includes(q);
    }).slice(0, 100);
  }, [modalLookupList, lookupQ]);

  // Tab 1 (Aturan) Filtered
  const filteredAturan = useMemo(() => jasaList.filter(item => {
    const qm = !searchQuery || (item.nm_tindakan || '').toLowerCase().includes(searchQuery.toLowerCase()) || (item.kd_tindakan || '').toLowerCase().includes(searchQuery.toLowerCase());
    const jm = filterJenis === 'ALL' || (item.jenis || '').toLowerCase().includes(filterJenis.toLowerCase());
    return qm && jm;
  }), [jasaList, searchQuery, filterJenis]);

  const totalPagesAturan = Math.max(1, Math.ceil(filteredAturan.length / ITEMS));
  const pagedAturan = filteredAturan.slice((currentPage - 1) * ITEMS, currentPage * ITEMS);

  // Tab 2 (Tindakan) Filtered
  const filteredTindakan = useMemo(() => tindakanList.filter(item => {
    if (!searchTindakan) return true;
    const q = searchTindakan.toLowerCase();
    return (item.kd_tindakan || '').toLowerCase().includes(q) || (item.nm_tindakan || '').toLowerCase().includes(q);
  }), [tindakanList, searchTindakan]);

  const totalPagesTindakan = Math.max(1, Math.ceil(filteredTindakan.length / ITEMS));
  const pagedTindakan = filteredTindakan.slice((pageTindakan - 1) * ITEMS, pageTindakan * ITEMS);

  // Tab 3 (Pendamping) Filtered
  const filteredPendamping = useMemo(() => pendampingList.filter(item => {
    if (!searchPendamping) return true;
    const q = searchPendamping.toLowerCase();
    const kode = (item.kd_posisi || item.kd_tindakan || '').toLowerCase();
    const nama = (item.posisi || item.nm_tindakan || '').toLowerCase();
    return kode.includes(q) || nama.includes(q);
  }), [pendampingList, searchPendamping]);

  const totalPagesPendamping = Math.max(1, Math.ceil(filteredPendamping.length / ITEMS));
  const pagedPendamping = filteredPendamping.slice((pagePendamping - 1) * ITEMS, pagePendamping * ITEMS);

  const inputFocusProps = {
    onFocus: e => { e.target.style.borderColor = COLOR.primary; e.target.style.boxShadow = `0 0 0 2px ${COLOR.primaryLt}`; },
    onBlur:  e => { e.target.style.borderColor = COLOR.slate200; e.target.style.boxShadow = 'none'; },
  };

  return (
    <div style={{ background: COLOR.slate100, minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: "'Inter','Segoe UI',sans-serif", paddingBottom: '6px' }}>

      {/* TOAST NOTIFICATION */}
      {toast.show && (
        <div style={{
          position: 'fixed', top: '12px', right: '16px', zIndex: 9999,
          background: toast.type === 'success' ? '#022c22' : toast.type === 'warning' ? '#451a03' : '#450a0a',
          color: COLOR.white, borderRadius: '6px', padding: '8px 14px',
          boxShadow: '0 4px 15px rgba(0,0,0,.2)',
          display: 'flex', alignItems: 'center', gap: '8px', minWidth: '260px',
          borderLeft: `4px solid ${toast.type === 'success' ? '#10b981' : toast.type === 'warning' ? '#f59e0b' : '#ef4444'}`,
          animation: 'slideIn .2s ease',
        }}>
          <i className={`bi ${toast.type === 'success' ? 'bi-check-circle-fill' : toast.type === 'warning' ? 'bi-exclamation-triangle-fill' : 'bi-x-circle-fill'}`}
            style={{ fontSize: '0.9rem', color: toast.type === 'success' ? '#10b981' : toast.type === 'warning' ? '#f59e0b' : '#ef4444' }} />
          <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>{toast.msg}</span>
        </div>
      )}

      {/* HEADER TOPBAR (VIBRANT TEAL THEME) */}
      <div style={{ background: COLOR.topHeaderBg, borderBottom: `2px solid ${COLOR.primaryDk}`, boxShadow: '0 1px 4px rgba(13, 148, 136, 0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '44px', padding: '0 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '6px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="bi bi-gear-wide-connected" style={{ fontSize: '0.9rem', color: COLOR.white }} />
            </div>
            <div>
              <span style={{ color: COLOR.white, fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.3px' }}>
                ATURAN JASA MEDIS PASIEN BPJS
              </span>
              <span className="d-none d-md-inline" style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.68rem', marginLeft: '8px' }}>
                — Master Aturan Jasa Medis, Master Tindakan &amp; Pendamping
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#ccfbf1', fontSize: '0.68rem', fontWeight: 600 }}>{clock.date}</span>
              <span style={{ color: COLOR.white, fontSize: '0.75rem', fontWeight: 800, fontFamily: 'monospace' }}>{clock.time} WIB</span>
            </div>
            <button
              onClick={() => navigate('/home')}
              style={{ ...S.btnGhost, height: '24px', padding: '2px 8px', fontSize: '0.7rem', borderColor: 'rgba(255,255,255,0.4)', color: COLOR.white, background: 'rgba(255,255,255,0.15)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            >
              <i className="bi bi-arrow-left" /> Kembali
            </button>
          </div>
        </div>
      </div>

      {/* MODERN TAB NAVIGATION BAR */}
      <div style={{
        background: COLOR.white, borderBottom: `1px solid ${COLOR.slate200}`,
        padding: '4px 16px 0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {/* Tab 1: Master Tindakan */}
          <button
            onClick={() => setActiveTab('tindakan')}
            style={{
              border: 'none', background: 'transparent',
              padding: '6px 14px', fontSize: '0.78rem', fontWeight: 700,
              color: activeTab === 'tindakan' ? COLOR.primary : COLOR.slate600,
              borderBottom: `2.5px solid ${activeTab === 'tindakan' ? COLOR.primary : 'transparent'}`,
              display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
              transition: 'all .15s'
            }}
          >
            <i className="bi bi-file-earmark-medical" style={{ color: activeTab === 'tindakan' ? COLOR.primary : COLOR.slate400 }} />
            <span>Master Tindakan (show_tindakan)</span>
            <span style={{
              background: activeTab === 'tindakan' ? COLOR.primaryLt : COLOR.slate100,
              color: activeTab === 'tindakan' ? COLOR.primary : COLOR.slate600,
              borderRadius: '10px', padding: '1px 6px', fontSize: '0.66rem', fontWeight: 800
            }}>
              {tindakanList.length}
            </span>
          </button>

          {/* Tab 2: Master Pendamping */}
          <button
            onClick={() => setActiveTab('pendamping')}
            style={{
              border: 'none', background: 'transparent',
              padding: '6px 14px', fontSize: '0.78rem', fontWeight: 700,
              color: activeTab === 'pendamping' ? COLOR.primary : COLOR.slate600,
              borderBottom: `2.5px solid ${activeTab === 'pendamping' ? COLOR.primary : 'transparent'}`,
              display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
              transition: 'all .15s'
            }}
          >
            <i className="bi bi-people" style={{ color: activeTab === 'pendamping' ? COLOR.primary : COLOR.slate400 }} />
            <span>Master Pendamping (show_pendamping)</span>
            <span style={{
              background: activeTab === 'pendamping' ? COLOR.primaryLt : COLOR.slate100,
              color: activeTab === 'pendamping' ? COLOR.primary : COLOR.slate600,
              borderRadius: '10px', padding: '1px 6px', fontSize: '0.66rem', fontWeight: 800
            }}>
              {pendampingList.length}
            </span>
          </button>

          {/* Tab 3: Aturan Jasa Medis BPJS (Di Belakang) */}
          <button
            onClick={() => setActiveTab('aturan')}
            style={{
              border: 'none', background: 'transparent',
              padding: '6px 14px', fontSize: '0.78rem', fontWeight: 700,
              color: activeTab === 'aturan' ? COLOR.primary : COLOR.slate600,
              borderBottom: `2.5px solid ${activeTab === 'aturan' ? COLOR.primary : 'transparent'}`,
              display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
              transition: 'all .15s'
            }}
          >
            <i className="bi bi-sliders2" style={{ color: activeTab === 'aturan' ? COLOR.primary : COLOR.slate400 }} />
            <span>Aturan Jasa Medis BPJS</span>
            <span style={{
              background: activeTab === 'aturan' ? COLOR.primaryLt : COLOR.slate100,
              color: activeTab === 'aturan' ? COLOR.primary : COLOR.slate600,
              borderRadius: '10px', padding: '1px 6px', fontSize: '0.66rem', fontWeight: 800
            }}>
              {jasaList.length}
            </span>
          </button>
        </div>
      </div>

      {/* MAIN CONTAINER BODY */}
      <div style={{ padding: '8px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>

        {/* ══════════════ TAB 1: ATURAN JASA MEDIS BPJS ══════════════ */}
        {activeTab === 'aturan' && (
          <>
            {/* Form Input Card */}
            <div ref={formRef} style={{
              ...S.card, overflow: 'hidden',
              borderLeft: `3px solid ${isEditing ? COLOR.warn : COLOR.primary}`,
            }}>
              <div style={{
                padding: '4px 12px', borderBottom: `1px solid ${COLOR.slate100}`,
                background: isEditing ? COLOR.warnLt : COLOR.slate50,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className={`bi ${isEditing ? 'bi-pencil-fill' : 'bi-plus-circle-fill'}`}
                    style={{ color: isEditing ? COLOR.warn : COLOR.primary, fontSize: '0.78rem' }} />
                  <span style={{ fontWeight: 700, fontSize: '0.74rem', color: COLOR.slate800 }}>
                    {isEditing ? 'Edit Aturan Jasa Medis' : 'Input / Tambah Aturan Pelayanan'}
                  </span>
                </div>
                {isEditing && (
                  <span style={{
                    background: COLOR.warn, color: COLOR.white,
                    borderRadius: '10px', padding: '1px 8px', fontSize: '0.64rem', fontWeight: 700,
                  }}>
                    MODE EDIT ({formData.kd_tindakan})
                  </span>
                )}
              </div>

              <form onSubmit={handleSave} style={{ padding: '8px 12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.3fr 2.6fr 0.9fr 0.9fr 0.9fr auto', gap: '8px', alignItems: 'end' }}>

                  {/* Jenis */}
                  <div>
                    <label style={S.label}>Jenis Pelayanan</label>
                    <select
                      value={formData.jenis}
                      onChange={e => handleJenisChange(e.target.value)}
                      style={S.input} {...inputFocusProps}
                    >
                      <option value="Non Pendam">Non Pendamping</option>
                      <option value="Pendamping">Pendamping</option>
                    </select>
                  </div>

                  {/* Kode */}
                  <div>
                    <label style={S.label}>{isCurrentPendamping ? 'Kode Pendamping' : 'Kode Tindakan'}</label>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <input
                        type="text" placeholder="Kode..." value={formData.kd_tindakan}
                        onChange={e => setFormData({ ...formData, kd_tindakan: e.target.value.toUpperCase() })}
                        disabled={isEditing}
                        style={{ ...S.input, fontWeight: 700, fontFamily: 'monospace', flex: 1, opacity: isEditing ? 0.6 : 1 }}
                        {...(!isEditing ? inputFocusProps : {})}
                      />
                      <button type="button" onClick={handleOpenLookup}
                        style={{
                          border: `1px solid ${COLOR.slate200}`,
                          borderRadius: '5px',
                          background: isCurrentPendamping ? COLOR.infoLt : COLOR.slate50,
                          color: isCurrentPendamping ? COLOR.info : COLOR.slate600,
                          padding: '0 8px', height: '28px', cursor: 'pointer'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = COLOR.slate100}
                        onMouseLeave={e => e.currentTarget.style.background = isCurrentPendamping ? COLOR.infoLt : COLOR.slate50}
                        title={isCurrentPendamping ? "Cari dari Master Pendamping (/show_pendamping)" : "Cari dari Master Tindakan (/show_mastertindakan)"}
                      >
                        <i className="bi bi-search" style={{ fontSize: '0.72rem' }} />
                      </button>
                    </div>
                  </div>

                  {/* Nama */}
                  <div>
                    <label style={S.label}>{isCurrentPendamping ? 'Nama Pendamping / Posisi' : 'Nama Pelayanan / Tindakan'}</label>
                    <input type="text" placeholder={isCurrentPendamping ? "Nama pendamping / posisi..." : "Nama pelayanan / tindakan..."}
                      value={formData.nm_tindakan}
                      onChange={e => setFormData({ ...formData, nm_tindakan: e.target.value })}
                      style={S.input} {...inputFocusProps}
                    />
                  </div>

                  {/* Non Tindakan */}
                  <div>
                    <label style={S.label} title="Non Tindakan (%)">Non Tndk (%)</label>
                    <NumInput value={formData.tarifnontindakan} onChange={v => setFormData({ ...formData, tarifnontindakan: v })} />
                  </div>

                  {/* Tindakan Non Visit */}
                  <div>
                    <label style={S.label} title="Tindakan Non Visit (%)">Tndk Non Vst (%)</label>
                    <NumInput value={formData.tariftindakan} onChange={v => setFormData({ ...formData, tariftindakan: v })} />
                  </div>

                  {/* Tindakan Dgn Visit */}
                  <div>
                    <label style={S.label} title="Tindakan Dgn Visit (%)">Tndk Dgn Vst (%)</label>
                    <NumInput value={formData.tarifbiasa} onChange={v => setFormData({ ...formData, tarifbiasa: v })} />
                  </div>

                  {/* Buttons */}
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button type="submit" disabled={isSaving}
                      style={{ ...S.btnPrimary, opacity: isSaving ? 0.7 : 1 }}
                      onMouseEnter={e => { if (!isSaving) e.currentTarget.style.opacity = '0.88'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = isSaving ? '0.7' : '1'; }}
                    >
                      {isSaving ? <Spinner animation="border" size="sm" /> : <i className={`bi ${isEditing ? 'bi-check-lg' : 'bi-plus-lg'}`} />}
                      {isEditing ? 'Update' : 'Simpan'}
                    </button>
                    {isEditing && (
                      <button type="button" onClick={resetForm} style={S.btnGhost}
                        onMouseEnter={e => e.currentTarget.style.background = COLOR.slate50}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        Batal
                      </button>
                    )}
                  </div>

                </div>
              </form>
            </div>

            {/* Table Card (Clean Light Slate Header) */}
            <div style={{ ...S.card, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, border: `1px solid ${COLOR.slate200}` }}>
              <div style={{
                padding: '6px 12px', borderBottom: `1px solid ${COLOR.slate100}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px',
                background: COLOR.white,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ position: 'relative' }}>
                    <i className="bi bi-search" style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: COLOR.slate400, fontSize: '0.72rem', pointerEvents: 'none' }} />
                    <input type="text" placeholder="Cari nama atau kode..."
                      value={searchQuery}
                      onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                      style={{ ...S.input, paddingLeft: '24px', width: '220px', height: '26px', fontSize: '0.74rem' }}
                      onFocus={e => { e.target.style.borderColor = COLOR.primary; e.target.style.boxShadow = `0 0 0 2px ${COLOR.primaryLt}`; }}
                      onBlur={e => { e.target.style.borderColor = COLOR.slate200; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <select value={filterJenis}
                    onChange={e => { setFilterJenis(e.target.value); setCurrentPage(1); }}
                    style={{ ...S.input, width: '140px', height: '26px', fontSize: '0.74rem' }}
                    onFocus={e => { e.target.style.borderColor = COLOR.primary; e.target.style.boxShadow = `0 0 0 2px ${COLOR.primaryLt}`; }}
                    onBlur={e => { e.target.style.borderColor = COLOR.slate200; e.target.style.boxShadow = 'none'; }}
                  >
                    <option value="ALL">Semua Jenis</option>
                    <option value="Non Pendam">Non Pendamping</option>
                    <option value="Pendamping">Pendamping</option>
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: COLOR.slate600 }}>
                    Total: <span style={{ color: COLOR.primary, fontWeight: 800 }}>{filteredAturan.length}</span> Aturan
                  </span>
                  <button onClick={fetchAturanData} disabled={isLoadingAturan}
                    style={{ ...S.btnGhost, fontSize: '0.7rem', padding: '2px 8px', height: '25px', opacity: isLoadingAturan ? 0.6 : 1 }}
                    onMouseEnter={e => { if (!isLoadingAturan) e.currentTarget.style.background = COLOR.slate50; }}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className={`bi bi-arrow-clockwise${isLoadingAturan ? ' spin' : ''}`} /> Refresh
                  </button>
                </div>
              </div>

              {/* Table with Standard Clean Header */}
              <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 355px)', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                    <tr style={{ background: COLOR.headerBg, color: COLOR.headerText, textAlign: 'center' }}>
                      <th rowSpan={2} style={{ padding: '7px 10px', borderRight: `1px solid ${COLOR.headerBorder}`, borderBottom: `2px solid ${COLOR.headerBorder}`, verticalAlign: 'middle', width: '130px', fontWeight: 700, letterSpacing: '0.3px', background: COLOR.headerBg }}>
                        JENIS PELAYANAN
                      </th>
                      <th rowSpan={2} style={{ padding: '7px 8px', borderRight: `1px solid ${COLOR.headerBorder}`, borderBottom: `2px solid ${COLOR.headerBorder}`, verticalAlign: 'middle', width: '80px', fontWeight: 700, letterSpacing: '0.3px', background: COLOR.headerBg }}>
                        KODE
                      </th>
                      <th rowSpan={2} style={{ padding: '7px 12px', borderRight: `1px solid ${COLOR.headerBorder}`, borderBottom: `2px solid ${COLOR.headerBorder}`, verticalAlign: 'middle', textAlign: 'left', fontWeight: 700, letterSpacing: '0.3px', background: COLOR.headerBg }}>
                        NAMA PELAYANAN / TINDAKAN
                      </th>
                      <th colSpan={3} style={{
                        padding: '4px 10px',
                        background: '#f1f5f9',
                        borderRight: `1px solid ${COLOR.headerBorder}`,
                        borderBottom: `1px solid ${COLOR.headerBorder}`,
                        textAlign: 'center'
                      }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                          background: '#fee2e2', color: '#b91c1c',
                          border: '1px solid #fca5a5',
                          borderRadius: '12px', padding: '1px 10px',
                          fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.5px'
                        }}>
                          <i className="bi bi-hospital" style={{ fontSize: '0.72rem' }} />
                          RAWAT INAP
                        </span>
                      </th>
                      <th rowSpan={2} style={{ padding: '7px 8px', borderBottom: `2px solid ${COLOR.headerBorder}`, verticalAlign: 'middle', width: '75px', fontWeight: 700, letterSpacing: '0.3px', background: COLOR.headerBg }}>
                        AKSI
                      </th>
                    </tr>

                    <tr style={{ background: COLOR.headerBgSub, color: COLOR.slate600, textAlign: 'center', fontSize: '0.68rem' }}>
                      <th style={{ padding: '4px 6px', borderRight: `1px solid ${COLOR.headerBorder}`, borderBottom: `2px solid ${COLOR.headerBorder}`, fontWeight: 700, width: '105px', background: COLOR.headerBgSub, letterSpacing: '0.2px' }}>
                        NON TINDAKAN (%)
                      </th>
                      <th style={{ padding: '4px 6px', borderRight: `1px solid ${COLOR.headerBorder}`, borderBottom: `2px solid ${COLOR.headerBorder}`, fontWeight: 700, width: '130px', background: COLOR.headerBgSub, letterSpacing: '0.2px' }}>
                        TNDK NON VISIT (%)
                      </th>
                      <th style={{ padding: '4px 6px', borderRight: `1px solid ${COLOR.headerBorder}`, borderBottom: `2px solid ${COLOR.headerBorder}`, fontWeight: 700, width: '130px', background: COLOR.headerBgSub, letterSpacing: '0.2px' }}>
                        TNDK DGN VISIT (%)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingAturan ? (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: COLOR.slate400 }}>
                        <Spinner animation="border" size="sm" style={{ marginRight: '6px' }} /> Memuat aturan jasa medis...
                      </td></tr>
                    ) : pagedAturan.length > 0 ? pagedAturan.map((item, idx) => (
                      <tr key={item.kd_tindakan || idx}
                        style={{ background: idx % 2 === 0 ? COLOR.white : COLOR.slate50, transition: 'background .1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0fdf8'}
                        onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? COLOR.white : COLOR.slate50}
                      >
                        <td style={{ padding: '6px 8px', textAlign: 'center', borderRight: `1px solid ${COLOR.slate100}` }}><JenisBadge jenis={item.jenis} /></td>
                        <td style={{ padding: '6px 6px', textAlign: 'center', borderRight: `1px solid ${COLOR.slate100}`, fontFamily: 'monospace', fontWeight: 800, color: COLOR.primaryDk }}>{item.kd_tindakan}</td>
                        <td style={{ padding: '6px 12px', borderRight: `1px solid ${COLOR.slate100}`, fontWeight: 600, color: COLOR.slate800, whiteSpace: 'normal', lineHeight: 1.3 }}>{item.nm_tindakan}</td>
                        <td style={{ padding: '6px 4px', textAlign: 'center', borderRight: `1px solid ${COLOR.slate100}` }}><PercentBadge value={item.tarifnontindakan} /></td>
                        <td style={{ padding: '6px 4px', textAlign: 'center', borderRight: `1px solid ${COLOR.slate100}` }}><PercentBadge value={item.tariftindakan} /></td>
                        <td style={{ padding: '6px 4px', textAlign: 'center', borderRight: `1px solid ${COLOR.slate100}` }}><PercentBadge value={item.tarifbiasa} /></td>
                        <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                            <button onClick={() => editRow(item)} title="Edit"
                              style={{ width: '24px', height: '24px', borderRadius: '4px', border: `1px solid ${COLOR.info}30`, background: COLOR.infoLt, color: COLOR.info, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              onMouseEnter={e => { e.currentTarget.style.background = COLOR.info; e.currentTarget.style.color = COLOR.white; }}
                              onMouseLeave={e => { e.currentTarget.style.background = COLOR.infoLt; e.currentTarget.style.color = COLOR.info; }}
                            ><i className="bi bi-pencil-fill" style={{ fontSize: '0.66rem' }} /></button>
                            <button onClick={() => handleDelete(item)} title="Hapus"
                              style={{ width: '24px', height: '24px', borderRadius: '4px', border: `1px solid ${COLOR.danger}30`, background: COLOR.dangerLt, color: COLOR.danger, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              onMouseEnter={e => { e.currentTarget.style.background = COLOR.danger; e.currentTarget.style.color = COLOR.white; }}
                              onMouseLeave={e => { e.currentTarget.style.background = COLOR.dangerLt; e.currentTarget.style.color = COLOR.danger; }}
                            ><i className="bi bi-trash-fill" style={{ fontSize: '0.66rem' }} /></button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: COLOR.slate400 }}>
                        <i className="bi bi-inbox" style={{ fontSize: '1.4rem', display: 'block', marginBottom: '4px' }} />
                        Tidak ada data yang sesuai filter
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalPagesAturan > 1 && (
                <div style={{ padding: '4px 12px', borderTop: `1px solid ${COLOR.slate100}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: COLOR.slate50 }}>
                  <span style={{ fontSize: '0.7rem', color: COLOR.slate400, fontWeight: 600 }}>
                    Hal {currentPage}/{totalPagesAturan} ({filteredAturan.length} data)
                  </span>
                  <Pagination size="sm" className="mb-0" style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
                    <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} />
                    {Array.from({ length: totalPagesAturan }, (_, i) => i + 1).map(p => (
                      <Pagination.Item key={p} active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                    ))}
                    <Pagination.Next disabled={currentPage === totalPagesAturan} onClick={() => setCurrentPage(p => Math.min(p + 1, totalPagesAturan))} />
                  </Pagination>
                </div>
              )}
            </div>

            {/* Global Settings Bar */}
            <div style={{
              ...S.card, overflow: 'hidden', borderLeft: `4px solid ${COLOR.danger}`,
              background: COLOR.white, padding: '6px 12px',
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: COLOR.dangerDk, fontWeight: 800, fontSize: '0.74rem' }}>
                    <i className="bi bi-sliders2" />
                    <span>SETTING BPJS:</span>
                  </div>

                  <SettingCard label="Dokter Operator" value={settingMedis.operator} onChange={v => setSettingMedis({ ...settingMedis, operator: v })} accent={COLOR.primary} light={COLOR.primaryLt} />

                  <div style={{ width: '1px', height: '22px', background: COLOR.slate200 }} />

                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <SettingCard label="Tindakan 1" value={settingMedis.operator1} onChange={v => setSettingMedis({ ...settingMedis, operator1: v })} accent={COLOR.warn} light={COLOR.warnLt} />
                    <SettingCard label="Tindakan 2" value={settingMedis.operator2} onChange={v => setSettingMedis({ ...settingMedis, operator2: v })} accent={COLOR.warn} light={COLOR.warnLt} />
                  </div>

                  <div style={{ width: '1px', height: '22px', background: COLOR.slate200 }} />

                  <SettingCard label="Naik Kelas" value={settingMedis.naikbpjs} onChange={v => setSettingMedis({ ...settingMedis, naikbpjs: v })} accent={COLOR.info} light={COLOR.infoLt} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button onClick={handleSaveSetting} disabled={isSavingSetting}
                    style={{ ...S.btnPrimary, height: '26px', fontSize: '0.74rem', padding: '0 12px', opacity: isSavingSetting ? 0.7 : 1 }}
                    onMouseEnter={e => { if (!isSavingSetting) e.currentTarget.style.opacity = '0.88'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = isSavingSetting ? '0.7' : '1'; }}
                  >
                    {isSavingSetting ? <Spinner animation="border" size="sm" /> : <i className="bi bi-check2-circle" />}
                    Simpan Setting
                  </button>
                  <button onClick={() => navigate('/home')}
                    style={{ ...S.btnDanger, height: '26px', fontSize: '0.74rem', padding: '0 12px' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    <i className="bi bi-power" /> KELUAR
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ══════════════ TAB 2: MASTER TINDAKAN (/show_tindakan) ══════════════ */}
        {activeTab === 'tindakan' && (
          <div style={{ ...S.card, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, border: `1px solid ${COLOR.slate200}` }}>
            <div style={{
              padding: '8px 14px', borderBottom: `1px solid ${COLOR.slate100}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px',
              background: COLOR.white,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ position: 'relative' }}>
                  <i className="bi bi-search" style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: COLOR.slate400, fontSize: '0.74rem', pointerEvents: 'none' }} />
                  <input type="text" placeholder="Cari kode atau nama tindakan..."
                    value={searchTindakan}
                    onChange={e => { setSearchTindakan(e.target.value); setPageTindakan(1); }}
                    style={{ ...S.input, paddingLeft: '26px', width: '260px', height: '28px', fontSize: '0.76rem' }}
                    onFocus={e => { e.target.style.borderColor = COLOR.primary; e.target.style.boxShadow = `0 0 0 2px ${COLOR.primaryLt}`; }}
                    onBlur={e => { e.target.style.borderColor = COLOR.slate200; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                <span style={{ fontSize: '0.74rem', color: COLOR.slate600, fontWeight: 700 }}>
                  Endpoint: <code style={{ color: COLOR.primary, background: COLOR.primaryLt, padding: '2px 6px', borderRadius: '4px' }}>/show_tindakan</code>
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.74rem', fontWeight: 700, color: COLOR.slate600 }}>
                  Total: <span style={{ color: COLOR.primary, fontWeight: 800 }}>{filteredTindakan.length}</span> Tindakan
                </span>
                <button onClick={fetchTindakanData} disabled={isLoadingTindakan}
                  style={{ ...S.btnGhost, fontSize: '0.72rem', padding: '3px 10px', height: '26px', opacity: isLoadingTindakan ? 0.6 : 1 }}
                >
                  <i className={`bi bi-arrow-clockwise${isLoadingTindakan ? ' spin' : ''}`} /> Refresh
                </button>
              </div>
            </div>

            <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: COLOR.headerBg, color: COLOR.headerText }}>
                  <tr style={{ borderBottom: `2px solid ${COLOR.headerBorder}` }}>
                    <th style={{ padding: '8px 10px', width: '60px', textAlign: 'center', borderRight: `1px solid ${COLOR.headerBorder}` }}>NO</th>
                    <th style={{ padding: '8px 10px', width: '110px', textAlign: 'center', borderRight: `1px solid ${COLOR.headerBorder}` }}>KODE TINDAKAN</th>
                    <th style={{ padding: '8px 14px', borderRight: `1px solid ${COLOR.headerBorder}`, textAlign: 'left' }}>NAMA TINDAKAN</th>
                    <th style={{ padding: '8px 12px', width: '200px', borderRight: `1px solid ${COLOR.headerBorder}` }}>NAMA PENDAMPING</th>
                    <th style={{ padding: '8px 12px', width: '140px', borderRight: `1px solid ${COLOR.headerBorder}` }}>VCLAIM</th>
                    <th style={{ padding: '8px 10px', width: '130px', textAlign: 'center' }}>AKSI</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingTindakan ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: COLOR.slate400 }}><Spinner animation="border" size="sm" /> Memuat data master tindakan...</td></tr>
                  ) : pagedTindakan.length > 0 ? pagedTindakan.map((t, idx) => (
                    <tr key={t.kd_tindakan || idx}
                      style={{ background: idx % 2 === 0 ? COLOR.white : COLOR.slate50, transition: 'background .1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0fdf8'}
                      onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? COLOR.white : COLOR.slate50}
                    >
                      <td style={{ padding: '6px 10px', textAlign: 'center', borderRight: `1px solid ${COLOR.slate100}`, color: COLOR.slate400 }}>
                        {(pageTindakan - 1) * ITEMS + idx + 1}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'center', borderRight: `1px solid ${COLOR.slate100}`, fontFamily: 'monospace', fontWeight: 800, color: COLOR.primaryDk }}>
                        {t.kd_tindakan}
                      </td>
                      <td style={{ padding: '6px 14px', borderRight: `1px solid ${COLOR.slate100}`, fontWeight: 600, color: COLOR.slate800 }}>
                        {t.nm_tindakan}
                      </td>
                      <td style={{ padding: '6px 12px', borderRight: `1px solid ${COLOR.slate100}`, color: COLOR.slate600 }}>
                        {t.nm_pendamping || '-'}
                      </td>
                      <td style={{ padding: '6px 12px', borderRight: `1px solid ${COLOR.slate100}`, color: COLOR.slate600 }}>
                        {t.vclaim ? <span className="badge bg-light text-dark border">{t.vclaim}</span> : '-'}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleSelectFromTindakanTab(t)}
                          style={{
                            ...S.btnPrimary, height: '24px', padding: '0 8px', fontSize: '0.68rem', borderRadius: '4px'
                          }}
                          title="Gunakan tindakan ini ke formulir Aturan"
                        >
                          <i className="bi bi-arrow-right-circle" /> Gunakan
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: COLOR.slate400 }}>
                      <i className="bi bi-inbox" style={{ fontSize: '1.6rem', display: 'block', marginBottom: '4px' }} />
                      Tidak ada data tindakan
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPagesTindakan > 1 && (
              <div style={{ padding: '6px 14px', borderTop: `1px solid ${COLOR.slate100}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: COLOR.slate50 }}>
                <span style={{ fontSize: '0.72rem', color: COLOR.slate600, fontWeight: 600 }}>
                  Halaman {pageTindakan} dari {totalPagesTindakan} ({filteredTindakan.length} tindakan)
                </span>
                <Pagination size="sm" className="mb-0" style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
                  <Pagination.Prev disabled={pageTindakan === 1} onClick={() => setPageTindakan(p => Math.max(p - 1, 1))} />
                  {Array.from({ length: totalPagesTindakan }, (_, i) => i + 1).map(p => (
                    <Pagination.Item key={p} active={p === pageTindakan} onClick={() => setPageTindakan(p)}>{p}</Pagination.Item>
                  ))}
                  <Pagination.Next disabled={pageTindakan === totalPagesTindakan} onClick={() => setPageTindakan(p => Math.min(p + 1, totalPagesTindakan))} />
                </Pagination>
              </div>
            )}
          </div>
        )}

        {/* ══════════════ TAB 3: MASTER PENDAMPING (/show_pendamping) ══════════════ */}
        {activeTab === 'pendamping' && (
          <div style={{ ...S.card, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, border: `1px solid ${COLOR.slate200}` }}>
            <div style={{
              padding: '8px 14px', borderBottom: `1px solid ${COLOR.slate100}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px',
              background: COLOR.white,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ position: 'relative' }}>
                  <i className="bi bi-search" style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: COLOR.slate400, fontSize: '0.74rem', pointerEvents: 'none' }} />
                  <input type="text" placeholder="Cari kode atau nama pendamping..."
                    value={searchPendamping}
                    onChange={e => { setSearchPendamping(e.target.value); setPagePendamping(1); }}
                    style={{ ...S.input, paddingLeft: '26px', width: '260px', height: '28px', fontSize: '0.76rem' }}
                    onFocus={e => { e.target.style.borderColor = COLOR.primary; e.target.style.boxShadow = `0 0 0 2px ${COLOR.primaryLt}`; }}
                    onBlur={e => { e.target.style.borderColor = COLOR.slate200; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                <span style={{ fontSize: '0.74rem', color: COLOR.slate600, fontWeight: 700 }}>
                  Endpoint: <code style={{ color: COLOR.info, background: COLOR.infoLt, padding: '2px 6px', borderRadius: '4px' }}>/show_pendamping</code>
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.74rem', fontWeight: 700, color: COLOR.slate600 }}>
                  Total: <span style={{ color: COLOR.primary, fontWeight: 800 }}>{filteredPendamping.length}</span> Pendamping
                </span>
                <button onClick={fetchPendampingData} disabled={isLoadingPendamping}
                  style={{ ...S.btnGhost, fontSize: '0.72rem', padding: '3px 10px', height: '26px', opacity: isLoadingPendamping ? 0.6 : 1 }}
                >
                  <i className={`bi bi-arrow-clockwise${isLoadingPendamping ? ' spin' : ''}`} /> Refresh
                </button>
              </div>
            </div>

            <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: COLOR.headerBg, color: COLOR.headerText }}>
                  <tr style={{ borderBottom: `2px solid ${COLOR.headerBorder}` }}>
                    <th style={{ padding: '8px 10px', width: '60px', textAlign: 'center', borderRight: `1px solid ${COLOR.headerBorder}` }}>NO</th>
                    <th style={{ padding: '8px 10px', width: '110px', textAlign: 'center', borderRight: `1px solid ${COLOR.headerBorder}` }}>KODE POSISI</th>
                    <th style={{ padding: '8px 14px', borderRight: `1px solid ${COLOR.headerBorder}`, textAlign: 'left' }}>POSISI / NAMA PENDAMPING</th>
                    <th style={{ padding: '8px 12px', width: '140px', borderRight: `1px solid ${COLOR.headerBorder}` }}>FLAG DOKTER</th>
                    <th style={{ padding: '8px 12px', width: '140px', borderRight: `1px solid ${COLOR.headerBorder}` }}>PELAKSANA</th>
                    <th style={{ padding: '8px 12px', width: '120px', borderRight: `1px solid ${COLOR.headerBorder}` }}>RINCI</th>
                    <th style={{ padding: '8px 10px', width: '130px', textAlign: 'center' }}>AKSI</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingPendamping ? (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: COLOR.slate400 }}><Spinner animation="border" size="sm" /> Memuat data master pendamping...</td></tr>
                  ) : pagedPendamping.length > 0 ? pagedPendamping.map((p, idx) => (
                    <tr key={p.kd_posisi || p.kd_tindakan || idx}
                      style={{ background: idx % 2 === 0 ? COLOR.white : COLOR.slate50, transition: 'background .1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0fdf8'}
                      onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? COLOR.white : COLOR.slate50}
                    >
                      <td style={{ padding: '6px 10px', textAlign: 'center', borderRight: `1px solid ${COLOR.slate100}`, color: COLOR.slate400 }}>
                        {(pagePendamping - 1) * ITEMS + idx + 1}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'center', borderRight: `1px solid ${COLOR.slate100}`, fontFamily: 'monospace', fontWeight: 800, color: COLOR.primaryDk }}>
                        {p.kd_posisi || p.kd_tindakan}
                      </td>
                      <td style={{ padding: '6px 14px', borderRight: `1px solid ${COLOR.slate100}`, fontWeight: 600, color: COLOR.slate800 }}>
                        {p.posisi || p.nm_tindakan}
                      </td>
                      <td style={{ padding: '6px 12px', borderRight: `1px solid ${COLOR.slate100}` }}>
                        {p.flag_dokter ? (
                          <span className="badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>Dokter</span>
                        ) : (
                          <span className="badge" style={{ background: '#f1f5f9', color: '#64748b' }}>Non Dokter</span>
                        )}
                      </td>
                      <td style={{ padding: '6px 12px', borderRight: `1px solid ${COLOR.slate100}`, color: COLOR.slate600 }}>
                        {p.pelaksana || '-'}
                      </td>
                      <td style={{ padding: '6px 12px', borderRight: `1px solid ${COLOR.slate100}`, color: COLOR.slate600 }}>
                        {p.rinci || '-'}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleSelectFromPendampingTab(p)}
                          style={{
                            ...S.btnPrimary, height: '24px', padding: '0 8px', fontSize: '0.68rem', borderRadius: '4px'
                          }}
                          title="Gunakan pendamping ini ke formulir Aturan"
                        >
                          <i className="bi bi-arrow-right-circle" /> Gunakan
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: COLOR.slate400 }}>
                      <i className="bi bi-inbox" style={{ display: 'block', fontSize: '1.6rem', marginBottom: '4px' }} />
                      Tidak ada data pendamping
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPagesPendamping > 1 && (
              <div style={{ padding: '6px 14px', borderTop: `1px solid ${COLOR.slate100}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: COLOR.slate50 }}>
                <span style={{ fontSize: '0.72rem', color: COLOR.slate600, fontWeight: 600 }}>
                  Halaman {pagePendamping} dari {totalPagesPendamping} ({filteredPendamping.length} pendamping)
                </span>
                <Pagination size="sm" className="mb-0" style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
                  <Pagination.Prev disabled={pagePendamping === 1} onClick={() => setPagePendamping(p => Math.max(p - 1, 1))} />
                  {Array.from({ length: totalPagesPendamping }, (_, i) => i + 1).map(p => (
                    <Pagination.Item key={p} active={p === pagePendamping} onClick={() => setPagePendamping(p)}>{p}</Pagination.Item>
                  ))}
                  <Pagination.Next disabled={pagePendamping === totalPagesPendamping} onClick={() => setPagePendamping(p => Math.min(p + 1, totalPagesPendamping))} />
                </Pagination>
              </div>
            )}
          </div>
        )}

      </div>

      {/* MODAL LOOKUP MASTER */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: COLOR.topHeaderBg, color: COLOR.white, padding: '8px 16px' }}>
          <Modal.Title style={{ color: COLOR.white, fontSize: '0.84rem', fontWeight: 700 }}>
            <i className="bi bi-search me-2" />
            {isCurrentPendamping ? 'Cari & Pilih Pendamping (show_pendamping)' : 'Cari & Pilih Master Tindakan (show_mastertindakan)'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '12px', background: COLOR.slate50 }}>
          <div style={{ position: 'relative', marginBottom: '8px' }}>
            <i className="bi bi-search" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: COLOR.slate400, fontSize: '0.78rem', pointerEvents: 'none' }} />
            <input autoFocus type="text"
              placeholder={isCurrentPendamping ? "Ketik kode posisi atau nama pendamping..." : "Ketik kode atau nama tindakan..."}
              value={lookupQ} onChange={e => setLookupQ(e.target.value)}
              style={{ ...S.input, paddingLeft: '30px', height: '28px' }}
              onFocus={e => { e.target.style.borderColor = COLOR.primary; e.target.style.boxShadow = `0 0 0 2px ${COLOR.primaryLt}`; }}
              onBlur={e => { e.target.style.borderColor = COLOR.slate200; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: `1px solid ${COLOR.slate200}`, borderRadius: '6px', background: COLOR.white }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
              <thead style={{ position: 'sticky', top: 0, background: COLOR.headerBg, color: COLOR.headerText, zIndex: 1 }}>
                <tr style={{ borderBottom: `2px solid ${COLOR.headerBorder}` }}>
                  <th style={{ padding: '6px 10px', width: '80px', fontWeight: 700 }}>Kode</th>
                  <th style={{ padding: '6px 10px', fontWeight: 700 }}>{isCurrentPendamping ? 'Nama Pendamping / Posisi' : 'Nama Tindakan'}</th>
                  <th style={{ padding: '6px 10px', width: '130px', fontWeight: 700 }}>{isCurrentPendamping ? 'Dokter / Pelaksana' : 'Pelaksana / VClaim'}</th>
                  <th style={{ padding: '6px 10px', width: '70px', textAlign: 'center', fontWeight: 700 }}>Pilih</th>
                </tr>
              </thead>
              <tbody>
                {isSearchingLookup ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: COLOR.slate400 }}>
                    <Spinner animation="border" size="sm" /> Memuat data {isCurrentPendamping ? 'pendamping' : 'tindakan'}...
                  </td></tr>
                ) : lookupFiltered.length > 0 ? lookupFiltered.map((t, i) => {
                  const kode = t.kd_tindakan || t.kd_posisi || '';
                  const nama = t.nm_tindakan || t.posisi || '';
                  const extra = isCurrentPendamping ? (t.flag_dokter ? `Dokter (${t.pelaksana || '-'})` : (t.pelaksana || '-')) : (t.vclaim || t.pelaksana || '-');
                  return (
                    <tr key={kode || i}
                      style={{ background: i % 2 === 0 ? COLOR.white : COLOR.slate50, cursor: 'pointer', transition: 'background .1s' }}
                      onClick={() => { setFormData(p => ({ ...p, kd_tindakan: kode, nm_tindakan: nama })); setShowModal(false); }}
                      onMouseEnter={e => e.currentTarget.style.background = COLOR.primaryLt}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? COLOR.white : COLOR.slate50}
                    >
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontWeight: 800, color: COLOR.primaryDk }}>{kode}</td>
                      <td style={{ padding: '6px 10px', fontWeight: 600, color: COLOR.slate800 }}>{nama}</td>
                      <td style={{ padding: '6px 10px', color: COLOR.slate600, fontSize: '0.7rem' }}>{extra}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                        <button
                          onClick={e => { e.stopPropagation(); setFormData(p => ({ ...p, kd_tindakan: kode, nm_tindakan: nama })); setShowModal(false); }}
                          style={{ ...S.btnPrimary, padding: '2px 8px', height: '22px', fontSize: '0.68rem', borderRadius: '4px' }}
                        >Pilih</button>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: COLOR.slate400 }}>
                    <i className="bi bi-inbox" style={{ display: 'block', fontSize: '1.4rem', marginBottom: '4px' }} />
                    Tidak ada data {isCurrentPendamping ? 'pendamping' : 'tindakan'} yang cocok
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '6px', color: COLOR.slate400, fontSize: '0.68rem', textAlign: 'right' }}>
            Menampilkan {lookupFiltered.length} dari {modalLookupList.length} data {isCurrentPendamping ? 'pendamping' : 'master tindakan'}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '6px 12px', background: COLOR.slate50, borderTop: `1px solid ${COLOR.slate100}` }}>
          <button onClick={() => setShowModal(false)} style={{ ...S.btnGhost, height: '24px', padding: '0 10px', fontSize: '0.72rem' }}>
            <i className="bi bi-x-lg" /> Tutup
          </button>
        </Modal.Footer>
      </Modal>

      <style>{`
        @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
        .spin { animation: spin .8s linear infinite; }
        @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
        table tbody tr td { border-bottom: 1px solid ${COLOR.slate100}; }
      `}</style>
    </div>
  );
};

export default AturanJasaMedisBPJS;
